'use client';

import { useState, useEffect } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import Link from 'next/link';
import Image from 'next/image';
import { MapPin } from 'lucide-react';
import { getStoredAnonymousId } from '@/lib/fcm-utils';
import UniqueVisitsIndicator from '@/components/UniqueVisitsIndicator';

interface Region {
  id?: string;
  name: string;
  slug: string;
  cruises_count: number;
  latitude?: number;
  longitude?: number;
  image_url?: string;
  image_author_name?: string;
  image_author_profile?: string;
  is_liked?: boolean;
  unique_visits?: number | null;
}

interface RegionsGridProps {
  className?: string;
}

const SkeletonCard = () => (
  <div className="h-[200px] bg-slate-200 dark:bg-slate-800 rounded-xl animate-pulse" />
);

export default function RegionsGrid({ className = '' }: RegionsGridProps) {
  const t = useTranslations('homePage');
  const locale = useLocale();
  const [regions, setRegions] = useState<Region[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRegions = async () => {
      try {
        setLoading(true);
        setError(null);
        const anonymousId = typeof window !== 'undefined' ? getStoredAnonymousId() : null;
        const query = new URLSearchParams({ locale });
        if (anonymousId) query.set('anonymous_id', anonymousId);
        const response = await fetch(`/api/regions/top?${query.toString()}`);

        if (!response.ok) {
          throw new Error(`Failed to load regions (${response.status})`);
        }

        const data = await response.json();
        const items = Array.isArray(data) ? data : data.items || [];
        const filteredAndSorted = items
          .filter((region: Region) => region.cruises_count > 0)
          .sort((a: Region, b: Region) => b.cruises_count - a.cruises_count)
          .slice(0, 6);

        setRegions(filteredAndSorted);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
        console.error('Failed to fetch top regions:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchRegions();
  }, [locale]);

  if (error) {
    return (
      <section className={`w-full py-12 px-4 ${className}`}>
        <div className="max-w-7xl mx-auto bg-white/85 dark:bg-slate-900/85 backdrop-blur-md rounded-3xl border border-slate-200 dark:border-slate-800 shadow-xl shadow-blue-900/5 dark:shadow-none p-8 md:p-12">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-300 text-center">
            {error}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`w-full py-12 px-4 ${className}`}>
      <div className="max-w-7xl mx-auto bg-white/85 dark:bg-slate-900/85 backdrop-blur-md rounded-3xl border border-slate-200 dark:border-slate-800 shadow-xl shadow-blue-900/5 dark:shadow-none p-8 md:p-12">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-slate-100 mb-3">
            {t('popularRegionsTitle')}
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            {t('popularRegionsSubtitle')}
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        )}

        {/* Regions Grid */}
        {!loading && regions.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {regions.map((region) => (
              <Link
                key={region.slug}
                href={`/regions/${region.slug}`}
                className="group"
              >
                <div className="relative h-[200px] rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                  <UniqueVisitsIndicator
                    variant="overlay"
                    uniqueVisits={region.unique_visits}
                  />
                  {/* Background Image or Gradient */}
                  {region.image_url ? (
                    <Image
                      src={region.image_url}
                      alt={region.name}
                      fill
                      className="object-cover"
                      unoptimized
                    />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-700 dark:from-blue-600 dark:to-blue-800">
                      {/* Background Pattern */}
                      <div className="absolute inset-0 opacity-20">
                        <div className="absolute inset-0" style={{
                          backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
                          backgroundSize: '24px 24px'
                        }} />
                      </div>
                    </div>
                  )}

                  {/* Overlay Gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

                  {/* Content */}
                  <div className="relative h-full flex flex-col justify-between p-6 text-white">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <MapPin className="w-5 h-5" />
                          <h3 className="text-xl font-bold leading-tight">
                            {region.name}
                          </h3>
                        </div>
                        {region.image_author_name && (
                          <p className="text-xs text-white/70">
                            Photo by {region.image_author_name}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Cruises Count Badge */}
                    <div className="flex items-center justify-between">
                      <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold bg-white/20 backdrop-blur-sm border border-white/30">
                        {region.cruises_count} {t('cruisesCountLabel', { count: region.cruises_count })}
                      </span>
                      <span className="text-white/80 group-hover:text-white transition-colors">
                        →
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && regions.length === 0 && (
          <div className="text-center py-12 text-slate-500 dark:text-slate-400">
            <p>{t('noRegionsAvailable') || 'No regions available at the moment.'}</p>
          </div>
        )}
      </div>
    </section>
  );
}

